// Local Storage for App and components' props

import ComponentsUNames from '../constants/componentsUNames'

const KEYS_SEPARATOR = '§'
const KEY_SSO_CONNECTION_IN_PROGRESS = 'SSO_CONNECTION_IN_PROGRESS'
const KEY_USER = 'USER'
const KEY_USER_COMPANY = 'USER_COMPANY'
const KEY_USER_STORE = 'USER_STORE'
const KEY_USER_SERVICE = 'USER_SERVICE'
const KEY_PREFS = 'PREFS'
const KEY_PARAMS_PATH_CUSTOMER_LOGO = 'PATH_CUSTOMER_LOGO'
const KEY_PERMISSIONS = 'PERMISSIONS'
// Validation levels for different kinds of orders
const KEY_LEVELS = 'LEVELS'
// Data from DataArea
const KEY_ROUTINE_MAINTENANCE_PARAMS = 'ROUTINE_MAINTENANCE_PARAMS'
const KEY_LANG = [KEY_PREFS, 'USERPREFS', 'LANG']
// Timestamp for the last server call (used for deciding whether session has timed-out locally or not)
const KEY_TIMESTAMP_LAST_SERVER_CALL = 'TIMESTAMP_LAST_SERVER_CALL'

const VALUE_TYPE = Object.freeze({
  STRING: 'String',
  INTEGER: 'Integer',
  BOOLEAN: 'Boolean',
  OBJECT: 'Object',
  NOT_FOUND: undefined,
})

const clear = () => {
  console.debug("Clearing local-storage (all user's preferences except language deleted)")
  localStorage.clear()
}

/**
 * Get from LocalStorage the property value, as 'valueType, which is stored under the flattened string-key made of tabKeys key-items
 * Note: values are stored under tabKeys as String (retrieved as such from backend storage)
 * If the key is not found, it will be created with the default value but only as far as user's prefs are concerned!
 * @param {*} tabKeys
 * @param {*} valueType
 * @param {*} defaultValue value returned in its expected <valueType>. If valueType is NOT_FOUND, returns undefined instead (no value)
 */
const getOrDefault = (tabKeys, valueType, defaultValue) => {
  // Default value to return
  let valueToReturn = valueType !== VALUE_TYPE.NOT_FOUND ? defaultValue : VALUE_TYPE.NOT_FOUND

  if (tabKeys.length > 0) {
    const flattenedKey = tabKeys.join(KEYS_SEPARATOR)
    const valueOfFlattenedKey = localStorage.getItem(flattenedKey)
    if (valueOfFlattenedKey !== null) {
      // Return value under appropriate type
      switch (valueType) {
        case VALUE_TYPE.BOOLEAN:
          valueToReturn = valueOfFlattenedKey === 'true'
          break
        case VALUE_TYPE.INTEGER:
          valueToReturn = parseInt(valueOfFlattenedKey)
          break
        case VALUE_TYPE.STRING:
          valueToReturn = valueOfFlattenedKey
          break
        default:
          break
      }
    } else {
      // Does not exist in local storage => create it if user-prefs (note: values are stored as strings in localStorage)
      if (flattenedKey.startsWith(KEY_PREFS)) {
        if (defaultValue !== VALUE_TYPE.NOT_FOUND) {
          localStorage.setItem(flattenedKey, defaultValue)
        } else {
          localStorage.removeItem(flattenedKey)
        }
      }
    }
  }

  return valueToReturn
}

/**
 * Flatten all leaf-values in valueToStore into a collection of key-sequences with these leaf-values in order to have the format expected in localStorage, i.e. {key: stringValue}.
 * 3 cases: valueToStore is an array (cf. "permissions"), or an object (cf. "prefs" which may be a Hashmap of HashMap), or a simple string
 * @param {*} tabKeys
 * @param {*} valueToStore
 */
const put = (tabKeys, valueToStore) => {
  if (typeof valueToStore === 'object' && valueToStore !== null) {
    if (Array.isArray(valueToStore)) {
      // Value passed as argument is an array. In this case we have only strings => we consider these as part of final key with value set to "true" (e.g. permissions)
      for (let i = 0; i < valueToStore.length; i++) {
        const element = valueToStore[i].toUpperCase()
        const allTabKeys = [...tabKeys, ...[element]]
        putAll(allTabKeys, 'true')
      }
    } else {
      // Value passed as argument is an object. It needs to be split-up into several {keys:.., value:..} items for being inserted into LocalStorage one after another
      const splitUpValues = splitUpObject(valueToStore)
      splitUpValues.forEach((item) => {
        const keys = item.keys
        const allTabKeys = [...tabKeys, ...keys]
        putAll(allTabKeys, item.value)
      })
    }
  } else {
    // Value passed as argument is not an object nor an array (string value) => we can it directly into localStorage after having joined its key-parts
    putAll(tabKeys, valueToStore)
  }
}

const splitUpObjectWithKeys = (tabKeys, element) => {
  let allFlattenedKeyValues = []
  if (typeof element === 'object' && element !== null) {
    for (const key in element) {
      if (Object.hasOwnProperty.call(element, key)) {
        const subTabKey = [key.toUpperCase()]
        const subElement = element[key]
        const flattenedKeyValues = splitUpObjectWithKeys([...tabKeys, ...subTabKey], subElement)
        allFlattenedKeyValues = [...allFlattenedKeyValues, ...flattenedKeyValues]
      }
    }
  } else {
    allFlattenedKeyValues.push({
      keys: tabKeys,
      value: element,
    })
  }

  return allFlattenedKeyValues
}

const splitUpObject = (objectValue) => {
  let allFlattenedKeyValues = []

  for (const key in objectValue) {
    if (Object.hasOwnProperty.call(objectValue, key)) {
      const element = objectValue[key]
      const flattenedKeyValues = splitUpObjectWithKeys([key], element)
      allFlattenedKeyValues = [...allFlattenedKeyValues, ...flattenedKeyValues]
    }
  }

  return allFlattenedKeyValues
}

/**
 * Saves valueToStore within the localStorage under the string-key made of concatenated individual tabKeys(and using a separator).
 * valueToStore are of the following types:
 * - string
 * - integer
 * - boolean
 * @param {} tabKeys
 * @param {*} valueToStore
 */
const putAll = (tabKeys, valueToStore) => {
  if (tabKeys.length > 0) {
    const flattenedKey = tabKeys.join(KEYS_SEPARATOR)
    if (valueToStore !== VALUE_TYPE.NOT_FOUND) {
      localStorage.setItem(flattenedKey, valueToStore)
    } else {
      localStorage.removeItem(flattenedKey)
    }
  }
}

const contains = (tabKeys) => {
  if (tabKeys.length > 0) {
    const flattenedKey = tabKeys.join(KEYS_SEPARATOR)
    const valueOfFlattenedKey = localStorage.getItem(flattenedKey)
    return valueOfFlattenedKey !== VALUE_TYPE.NOT_FOUND
  }
  return false
}

const getStoredExpanded = (id, uname) =>
  getOrDefault([KEY_PREFS, uname, id + '|' + ComponentsUNames.PROP_EXPANDED], VALUE_TYPE.BOOLEAN, true)

const getStoredIndex = (id, defaultIndex, unname) =>
  getOrDefault([KEY_PREFS, unname, id + '|' + ComponentsUNames.PROP_POSITION], VALUE_TYPE.INTEGER, defaultIndex)

const remove = (key1stLevel) => {
  if (localStorage.hasOwnProperty(key1stLevel)) {
    delete localStorage[key1stLevel]
  }
}

export default {
  KEYS_SEPARATOR,
  KEY_SSO_CONNECTION_IN_PROGRESS,
  KEY_USER,
  KEY_USER_COMPANY,
  KEY_USER_STORE,
  KEY_USER_SERVICE,
  KEY_PREFS,
  KEY_PARAMS_PATH_CUSTOMER_LOGO,
  KEY_PERMISSIONS,
  KEY_LEVELS,
  KEY_ROUTINE_MAINTENANCE_PARAMS,
  KEY_LANG,
  KEY_TIMESTAMP_LAST_SERVER_CALL,
  VALUE_TYPE,
  clear,
  getOrDefault,
  put,
  contains,
  getStoredExpanded,
  getStoredIndex,
  remove,
}
